import { HocuspocusProvider } from '@hocuspocus/provider';
import * as Y from 'yjs';
import { runYjsDocumentCompatMigrations } from '../lib/yjs/document-compat-migrations';
import { createUndoManager } from '../lib/yjs/undo-manager';
import { getFlowPermission } from './flow-permission-store';

let statelessNoticeHandler: ((noticeInfo: any) => void) | null = null;
export function setStatelessNoticeHandler(handler: (noticeInfo: any) => void) {
  statelessNoticeHandler = handler;
}

const rawServerUrl = import.meta.env.VITE_YJS_PATH;
// 强制将 http/https 转换为 ws/wss，兼容 Safari 严格的 WebSocket 协议检查
const serverUrl = rawServerUrl.replace(/^http:\/\//i, 'ws://').replace(/^https:\/\//i, 'wss://');

console.log('[Yjs] Current URL:', window.location.href);

export const ydoc = new Y.Doc();

console.log('[Yjs] Initializing data structures...');

// 创建 root Map 作为父容器，统一管理节点和边,只有y类型才能作为传递给undoredomanager
export const yRoot = ydoc.getMap('root');

export let yNodesMetadata = new Y.Map<unknown>();
export let yEdgesMap = new Y.Map<unknown>();

function refreshSharedMaps(createIfMissing = false) {
  const existingNodesMetadata = yRoot.get('nodesMetadata');
  if (existingNodesMetadata instanceof Y.Map) {
    yNodesMetadata = existingNodesMetadata as Y.Map<unknown>;
  } else if (createIfMissing) {
    const nextNodesMetadata = new Y.Map<unknown>();
    yRoot.set('nodesMetadata', nextNodesMetadata);
    yNodesMetadata = nextNodesMetadata;
  }

  const existingEdgesMap = yRoot.get('edgesMap');
  if (existingEdgesMap instanceof Y.Map) {
    yEdgesMap = existingEdgesMap as Y.Map<unknown>;
  } else if (createIfMissing) {
    const nextEdgesMap = new Y.Map<unknown>();
    yRoot.set('edgesMap', nextEdgesMap);
    yEdgesMap = nextEdgesMap;
  }
}

export function ensureSharedMapsAttached() {
  refreshSharedMaps(true);
}

// 每节点独立 Y.Map
export const yCustomNodeDatas = ydoc.getMap<Y.Map<unknown>>('customNodeDatas');

// 任务数据 Y.Map
export const yTaskInfo = ydoc.getMap<unknown>('taskInfo');

// 画布素材数据 Y.Map
export const yAssetsData = ydoc.getMap<Y.Map<unknown>>('assetsData');

// 各客户端独立的 viewport 数据 (key: uid, value: { x, y, zoom })
export const yViewports = ydoc.getMap('viewports');

console.log('[Yjs] Data structures initialized');

// Hocuspocus provider - 懒建于 connectProvider(projectId)，鉴权通过后才创建
export let provider: HocuspocusProvider | null = null;

// 等待首次同步完成的 Promise（deferred，由 connectProvider 内部 resolve）
let _resolveSync!: () => void;
export const synced: Promise<void> = new Promise(resolve => { _resolveSync = resolve; });

function prepareDocumentForEditing() {
  ensureSharedMapsAttached();
  createUndoManager();
}

function prepareSyncedDocument() {
  refreshSharedMaps();
  runYjsDocumentCompatMigrations({
    ydoc,
    canWrite: !getFlowPermission().isReadonly,
  });
  prepareDocumentForEditing();
}

function disconnectProviderInstance(targetProvider: HocuspocusProvider) {
  targetProvider.configuration.websocketProvider.stopConnectionAttempt();
  targetProvider.configuration.websocketProvider.cancelWebsocketRetry?.();
  targetProvider.configuration.websocketProvider.cancelWebsocketRetry = undefined;
  if (targetProvider.isAttached) {
    targetProvider.detach();
  }
  targetProvider.disconnect();
}

function finalizeSyncedDocument(targetProvider: HocuspocusProvider) {
  refreshSharedMaps();
  if (getFlowPermission().isReadonly) {
    disconnectProviderInstance(targetProvider);
  }
  prepareSyncedDocument();
}

function waitForProviderSync(targetProvider: HocuspocusProvider): Promise<void> {
  if (targetProvider.isSynced) {
    finalizeSyncedDocument(targetProvider);
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const handleSynced = ({ state }: { state: boolean }) => {
      if (!state) return;

      finalizeSyncedDocument(targetProvider);
      targetProvider.off('synced', handleSynced);
      resolve();
    };

    targetProvider.on('synced', handleSynced);
  });
}

let yjsTokenFetcher: ((projectId: string) => Promise<string>) | null = null;
export function setYjsTokenFetcher(fetcher: (projectId: string) => Promise<string>) {
  yjsTokenFetcher = fetcher;
}

// 在项目鉴权通过后调用，建立 Hocuspocus 协作连接
export function connectProvider(projectId: string) {
  // 如果已有 provider，先断开
  if (provider) {
    provider.destroy();
  }

  const nextProvider = new HocuspocusProvider({
    url: serverUrl,
    name: projectId,
    document: ydoc,
    token: async () => {
      try {
        if (yjsTokenFetcher) {
          const token = await yjsTokenFetcher(projectId);
          if (token) return token;
        }
        return '';
      } catch (e) {
        console.error('[Yjs] 请求鉴权 Token 异常', e);
        return '';
      }
    },
    onAuthenticationFailed: ({ reason }) => {
      console.log('[Yjs] 协作连接鉴权失败:', reason);
      if (statelessNoticeHandler) {
        statelessNoticeHandler({ type: 'message', info: { message: '用户连接鉴权失败，请重试' } });
      }
    }
  });

  // 监听当前画板的 stateless 消息 (用于无状态通知)
  nextProvider.on('stateless', ({ payload }: { payload: string }) => {
    try {
      const noticeInfo = JSON.parse(payload);
      console.log("[Canvas Notice] 收到实时通知:", noticeInfo);
      if (statelessNoticeHandler) {
        statelessNoticeHandler(noticeInfo);
      }
    } catch (e) {
      console.log("解析通知失败", e);
    }
  });

  provider = nextProvider;

  console.log('[Yjs] Connecting to server:', serverUrl);
  console.log('[Yjs] Room:', projectId);

  if (nextProvider.isSynced) {
    finalizeSyncedDocument(nextProvider);
    _resolveSync();
  } else {
    const handler = ({ state }: { state: boolean }) => {
      if (state) {
        finalizeSyncedDocument(nextProvider);
        nextProvider.off('synced', handler);
        _resolveSync();
      }
    };
    nextProvider.on('synced', handler);
  }

  nextProvider.on('synced', ({ state }: { state: boolean }) => {
    if (!state) return;

    refreshSharedMaps();
    console.log("✅ 客户端同步完成，数据已从服务器加载");
    // debugger; // 浏览器会在此暂停
    console.log("文档内容:", ydoc.toJSON());  // 或检查具体字段
    console.log("yRoot文档内容:", yRoot.toJSON());  // 或检查具体字段
    console.log('[Yjs] Provider synced');
    console.log('[Yjs] Server nodes count:', yNodesMetadata.size);
    console.log('[Yjs] Server edges count:', yEdgesMap.size);
  });

  nextProvider.on('status', (status: { status: string }) => {
    console.log('[Yjs] Provider status:', status.status);
  });
}

export function disconnectProvider() {
  if (!provider) {
    return;
  }

  disconnectProviderInstance(provider);
}

export function reconnectProvider(projectId: string) {
  if (provider && provider.configuration.name === projectId) {
    // 空闲断开后优先复用原 provider，保留当前文档和 awareness 上下文。
    if (!provider.isAttached) {
      provider.attach();
    }
    void provider.connect();

    return waitForProviderSync(provider);
  }

  // 重连后返回“本次连接完成”信号，供空闲断开确认框关闭时机使用。
  connectProvider(projectId);

  if (!provider) {
    return Promise.reject(new Error('Yjs provider was not created'));
  }

  return waitForProviderSync(provider);
}
